/**
 * The role carried by the ACCESS TOKEN.
 *
 * Deliberately NOT read from `localStorage.user_role` or from the `access_user`
 * blob the way `AdminRoute` and `AuthContext` do: both of those are plain
 * client-side storage that anyone can edit in devtools, and `AuthContext` goes
 * further and *guesses* a role from substrings in the user's name or email.
 * Good enough for deciding which menu to draw, useless for deciding which
 * dashboard someone may open.
 *
 * The token payload is the one role signal the client did not invent: the
 * approver sign-in (`/auth/approver-login`) issues a token tagged
 * `role: "approver"`, while the preparer sign-in issues one with no role at
 * all. So "is this an approver token" is the only distinction available here —
 * which happens to be exactly the one the two dashboards need.
 *
 * It is still only a NAVIGATION signal. The payload is read without verifying
 * the signature, because the browser has no key to verify it with. Treat every
 * answer from this module as a hint about what to render, never as permission
 * to do something — that belongs to the API.
 */
export function tokenRole(): string | null {
  const token =
    localStorage.getItem("access_token") ?? sessionStorage.getItem("access_token");
  if (!token) return null;

  try {
    // base64url → base64, same normalisation as useSessionManager's expiry read.
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64));
    const role = payload?.role;
    return typeof role === "string" ? role.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

export const isApproverToken = (): boolean => tokenRole() === "approver";
